import React, {useState} from "react";
import {Button, Descriptions, Dropdown, Menu, Row, Space, Tooltip, Typography} from "antd";
import {useNavigate} from "react-router";
import {Shipment} from "../../../models/parcels/shipment";
import Date from "../../date/date";
import {Link} from "react-router-dom";
import {shipmentsService} from "../../../services/shipments/shipments.service";
import {notifications} from "../../../services/notifications/notifications";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {EditModal} from "./edit-modal/edit-modal";
import {useTranslate} from "../../../hooks/translate.hook";
import {PageHeader} from "@ant-design/pro-components";
import {ShipmentsDispatchModal} from "../modal/shipments-dispatch/shipments-dispatch-modal";
import {ChangeCarrierModal} from "./modal/change-carrier-modal/change-carrier-modal";
import {downloadBlobFile} from "../../../utils/blob.download";
import {
    DownloadExclusiveLabelsModal
} from "../modal/shipment-download-exclusive-labels/shipment.download-exclusive-labels";
import {ShipmentStatus} from "../common/status/shipment.status";
import {useRequiredStore} from "../../../utils/store";
import {RootStoreContext} from "../../../stores/root/root.store";

interface InvoiceDataHeaderProps {
    shipment: Shipment;
    reload: () => void;
}

export const ShipmentHeader: React.FC<InvoiceDataHeaderProps> = ({shipment, reload}) => {
    const t = useTranslate();
    const {authStore: { generateUri }} = useRequiredStore(RootStoreContext);

    const navigate = useNavigate();

    const [loading, setLoading] = useState<boolean>(false)

    const [changeCarrierModalShown, setChangeCarrierModalShown] = useState<boolean>(false)

    const [dispatchModalShown, setDispatchModalShown] = useState<boolean>(false)

    const [downloadExclusiveLabelsModalShown, setDownloadExclusiveLabelsModalShown] = useState<boolean>(false)

    const [editModalShown, setEditModalShown] = useState<boolean>(false)

    const createStickers = async () => {
        setLoading(true);
        try {
            await shipmentsService.createStickers([shipment.id]);
            notifications.successfully();
        } catch (e: any) {
            notifications.message("error", e as string);
        } finally {
            setLoading(false);
        }
        reload();
    }

    const cancel = async () => {
        setLoading(true);
        try {
            await shipmentsService.cancelShipment(shipment.id);
            notifications.successfully();
        } catch (e: any) {
            notifications.message("error", e as string);
        } finally {
            setLoading(false);
        }
        reload();
    }

    const downloadConsignmentNote = async () => {
        setLoading(true);
        try {
            const {blob, filename} = await shipmentsService.downloadConsignmentNote(shipment.id);
            downloadBlobFile(blob, filename);
            notifications.successfully();
        } catch (e: any) {
            notifications.message("error", e as string);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <PageHeader
                className="invoice-header"
                style={{ padding: 0 }}
                ghost={false}
                title={
                    <Typography.Title level={1} style={{ margin: 0 }}>
                        {t("SHIPMENTS.ITEM.TITLE")}
                    </Typography.Title>
                }
                onBack={() => {
                    navigate("/shipments");
                }}
                extra={<ShipmentStatus status={shipment.status} large={true} />}
            >
                <Space direction="vertical" size={[15, 15]}>
                    <Row justify={"space-between"}>
                        <Space className={"invoice-data-header-buttons"} direction={"horizontal"} size={[20, 20]} wrap>
                            <Dropdown
                                placement={"bottomLeft"}
                                trigger={["click"]}
                                overlay={
                                    <Menu>
                                        {shipment.orders.map((order) => (
                                            <Menu.Item key={order.id}>
                                                <Link
                                                    style={{ textAlign: "left" }}
                                                    className={"ant-btn ant-btn-link"}
                                                    to={"/orders/" + order.id}
                                                >
                                                    {order.id} ({order.status})
                                                </Link>
                                            </Menu.Item>
                                        ))}
                                    </Menu>
                                }
                            >
                                <Button type="primary">{t("ORDERS.LIST")}</Button>
                            </Dropdown>
                        </Space>
                        <Space className={"invoice-data-header-buttons"} direction={"horizontal"} size={[20, 20]} wrap>
                            <Dropdown
                                placement={"bottomLeft"}
                                trigger={["click"]}
                                overlay={
                                    <Menu>
                                        <Menu.Item onClick={() => createStickers()}>
                                            Create labels
                                        </Menu.Item>
                                        <Menu.Item onClick={() => setChangeCarrierModalShown(true)}>
                                            Change carrier & Create labels
                                        </Menu.Item>
                                        <Menu.Item>
                                            <a href={generateUri("/orders/shipments/labels", "id[]=" + shipment.id)}>
                                                Download labels
                                            </a>
                                        </Menu.Item>
                                        {shipment.procedureName === 'autodoc' ? (
                                            <Menu.Item>
                                                <a href={generateUri("/api/v3/orders/shipments/" + shipment.id + "/autodoc-psl")}>
                                                    Download AutoDoc PSL label
                                                </a>
                                            </Menu.Item>
                                        ) : null}
                                        <Menu.Item onClick={() => setDownloadExclusiveLabelsModalShown(true)}>
                                            {t("SHIPMENTS.DOWNLOAD-EXCLUSIVE-LABELS.TITLE")}
                                        </Menu.Item>
                                        <Menu.Item onClick={() => downloadConsignmentNote()}>
                                            {t("SHIPMENTS.DOWNLOAD-CONSIGNMENT_NOTE.TITLE")}
                                        </Menu.Item>
                                        <Menu.Item onClick={() => setDispatchModalShown(true)}>
                                            {t("DISPATCH.DO_DISPATCH")}
                                        </Menu.Item>
                                        <Menu.Item onClick={() => cancel()}>
                                            Cancel
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button
                                    type="primary"
                                    loading={loading}
                                >
                                    {t("ACTIONS.TITLE")}</Button>
                            </Dropdown>
                        </Space>
                    </Row>

                    <div className="data-descriptions-with-edit-wrapper">
                        <div className="data-content-wrapper">
                            <Descriptions
                                className="data-content-wrapper-descriptions"
                                size={"small"}
                                layout={"vertical"}
                                contentStyle={{ fontSize: "17px" }}
                                labelStyle={{ color: "#858585" }}
                                column={{ xxl: 4, xl: 4, lg: 4, md: 3, sm: 2, xs: 1 }}
                            >
                                <Descriptions.Item label={t("SHIPMENTS.ITEM.TITLE")}>
                                    {shipment.number}
                                </Descriptions.Item>
                                <Descriptions.Item label={t("REFERENCE.TITLE")}>
                                    {shipment.reference}
                                </Descriptions.Item>
                                <Descriptions.Item label={t("SHIPMENTS.CARRIER.TITLE")}>
                                    {shipment.carrier.name}
                                </Descriptions.Item>
                                <Descriptions.Item label={t("WAREHOUSES.WAREHOUSE_TITLE")}>
                                    {shipment.warehouse.name}
                                </Descriptions.Item>
                                <Descriptions.Item label={t("DATE")}>
                                    <Date dateString={shipment.packedAt || shipment.createdAt} />
                                </Descriptions.Item>
                                <Descriptions.Item label={t("NETTO_WEIGHT")}>
                                    {shipment.netWeight} kg.
                                </Descriptions.Item>
                                <Descriptions.Item label={t("WEIGHT_BRUTTO")}>
                                    {shipment.grossWeight} kg.
                                </Descriptions.Item>
                            </Descriptions>
                            <Tooltip placement="topRight" title={t("EDIT")}>
                                <Button
                                    className="data-description-btn btn"
                                    onClick={() => setEditModalShown(true)}
                                    type="default"
                                    icon={<FontAwesomeIcon icon={["fas", "pencil-alt"]} />}
                                />
                            </Tooltip>
                        </div>
                    </div>
                </Space>
            </PageHeader>
            {changeCarrierModalShown ? (
                <ChangeCarrierModal
                    shipment={shipment}
                    onCancel={() => setChangeCarrierModalShown(false)}
                    onComplete={() => {
                        setChangeCarrierModalShown(false);
                        reload();
                        notifications.successfully();
                    }}
                />
            ) : null}
            {dispatchModalShown ? (
                <ShipmentsDispatchModal
                    shipments={[shipment.id]}
                    onClose={() => setDispatchModalShown(false)}
                    onSuccess={() => reload()}
                />
            ) : null}
            {editModalShown ? (
                <EditModal
                    shipment={shipment}
                    onClose={() => setEditModalShown(false)}
                    onComplete={() => reload()}
                />
            ) : null}
            {downloadExclusiveLabelsModalShown ? (
                <DownloadExclusiveLabelsModal
                    shipment={shipment}
                    onClose={() => setDownloadExclusiveLabelsModalShown(false)}
                />
            ) : null}
        </>
    );
}